import { useState, useMemo } from 'react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
} from 'recharts';
import { useAlerts, useAlertStats, useAcknowledgeAlert, useResolveAlert, useDismissAlert } from '../api/alerts';
import type { Alert, AlertSeverity, AlertStatus } from '../types';
import { format } from 'date-fns';

const SEVERITY_CONFIG: Record<AlertSeverity, { label: string; color: string; bg: string }> = {
  critical: { label: 'Critical', color: '#ef4444', bg: 'bg-red-500/10 text-red-400 border-red-500/30' },
  high: { label: 'High', color: '#f97316', bg: 'bg-orange-500/10 text-orange-400 border-orange-500/30' },
  medium: { label: 'Medium', color: '#eab308', bg: 'bg-amber-500/10 text-amber-400 border-amber-500/30' },
  low: { label: 'Low', color: '#3b82f6', bg: 'bg-blue-500/10 text-blue-400 border-blue-500/30' },
  info: { label: 'Info', color: '#06b6d4', bg: 'bg-cyan-500/10 text-cyan-400 border-cyan-500/30' },
};

const STATUS_CONFIG: Record<AlertStatus, { label: string; color: string }> = {
  new: { label: 'New', color: 'text-red-400' },
  acknowledged: { label: 'Acknowledged', color: 'text-amber-400' },
  in_progress: { label: 'Investigating', color: 'text-blue-400' },
  resolved: { label: 'Resolved', color: 'text-green-400' },
  dismissed: { label: 'Dismissed', color: 'text-gray-500' },
};

export default function AlertCenter() {
  const [page, setPage] = useState(1);
  const [severityFilter, setSeverityFilter] = useState<AlertSeverity | ''>('');
  const [statusFilter, setStatusFilter] = useState<AlertStatus | ''>('');
  const [selectedAlert, setSelectedAlert] = useState<Alert | null>(null);

  const { data: alertsData, isLoading } = useAlerts({
    page,
    page_size: 15,
    severity: severityFilter || undefined,
    status: statusFilter || undefined,
    sort_by: 'created_at',
    sort_order: 'desc',
  });

  const { data: stats } = useAlertStats();
  const acknowledgeMut = useAcknowledgeAlert();
  const resolveMut = useResolveAlert();
  const dismissMut = useDismissAlert();

  const alerts = alertsData?.items ?? [];
  const totalPages = alertsData?.total_pages ?? 1;

  const severityChartData = useMemo(() => {
    if (!stats?.by_severity) return [];
    return Object.entries(stats.by_severity).map(([sev, count]) => ({
      severity: sev.charAt(0).toUpperCase() + sev.slice(1),
      count,
      color: SEVERITY_CONFIG[sev as AlertSeverity]?.color || '#64748b',
    }));
  }, [stats]);

  function handleAction(alertId: string, action: 'acknowledge' | 'resolve' | 'dismiss') {
    const mutations = { acknowledge: acknowledgeMut, resolve: resolveMut, dismiss: dismissMut };
    mutations[action].mutate(alertId, {
      onSuccess: () => {
        setSelectedAlert(null);
      },
    });
  }

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-100">Alert Center</h1>
          <p className="text-sm text-gray-500 mt-1">
            {alertsData?.total ?? 0} alerts | {stats?.last_24h ?? 0} in last 24h
          </p>
        </div>
      </div>

      {/* Stats row */}
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
        {(['critical', 'high', 'medium', 'low', 'info'] as AlertSeverity[]).map((sev) => (
          <button
            key={sev}
            onClick={() => setSeverityFilter(severityFilter === sev ? '' : sev)}
            className={`stat-card text-center ${severityFilter === sev ? 'border-2' : ''} ${SEVERITY_CONFIG[sev].bg}`}
          >
            <p className="text-2xl font-bold font-mono">{stats?.by_severity?.[sev] ?? 0}</p>
            <p className="text-xs font-medium mt-1">{SEVERITY_CONFIG[sev].label}</p>
          </button>
        ))}
      </div>

      {/* Chart + Filters */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-1 glass-panel p-4">
          <h3 className="text-sm font-semibold text-gray-300 mb-3">Severity Distribution</h3>
          <ResponsiveContainer width="100%" height={180}>
            <BarChart data={severityChartData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#1e3a5f" />
              <XAxis dataKey="severity" tick={{ fill: '#64748b', fontSize: 10 }} />
              <YAxis tick={{ fill: '#64748b', fontSize: 10 }} />
              <Tooltip
                contentStyle={{
                  background: '#1a2744',
                  border: '1px solid #2d4a6f',
                  borderRadius: '8px',
                  fontSize: '12px',
                }}
              />
              <Bar dataKey="count" radius={[4, 4, 0, 0]}>
                {severityChartData.map((entry, i) => (
                  <Cell key={i} fill={entry.color} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div className="lg:col-span-2">
          {/* Filters */}
          <div className="flex flex-wrap items-center gap-3 mb-4">
            <select
              value={severityFilter}
              onChange={(e) => { setSeverityFilter(e.target.value as AlertSeverity | ''); setPage(1); }}
              className="px-3 py-2 bg-surface-tertiary border border-border-subtle rounded-lg text-sm text-gray-300 focus:outline-none focus:border-cyber-cyan/40"
            >
              <option value="">All Severities</option>
              {(['critical', 'high', 'medium', 'low', 'info'] as AlertSeverity[]).map((s) => (
                <option key={s} value={s}>{SEVERITY_CONFIG[s].label}</option>
              ))}
            </select>

            <select
              value={statusFilter}
              onChange={(e) => { setStatusFilter(e.target.value as AlertStatus | ''); setPage(1); }}
              className="px-3 py-2 bg-surface-tertiary border border-border-subtle rounded-lg text-sm text-gray-300 focus:outline-none focus:border-cyber-cyan/40"
            >
              <option value="">All Statuses</option>
              {(['new', 'acknowledged', 'in_progress', 'resolved', 'dismissed'] as AlertStatus[]).map((s) => (
                <option key={s} value={s}>{STATUS_CONFIG[s].label}</option>
              ))}
            </select>
          </div>

          {/* Alert list */}
          <div className="glass-panel divide-y divide-border-subtle">
            {isLoading ? (
              <div className="flex items-center justify-center py-12">
                <div className="w-6 h-6 border-2 border-cyber-cyan/30 border-t-cyber-cyan rounded-full animate-spin" />
              </div>
            ) : alerts.length === 0 ? (
              <p className="text-sm text-gray-500 text-center py-12">No alerts found</p>
            ) : (
              alerts.map((alert) => (
                <div
                  key={alert.id}
                  onClick={() => setSelectedAlert(alert)}
                  className={`flex items-start gap-3 p-4 cursor-pointer transition-colors hover:bg-surface-tertiary/50 ${
                    selectedAlert?.id === alert.id ? 'bg-helios-700/20' : ''
                  }`}
                >
                  {/* Severity indicator */}
                  <div
                    className="w-1 h-12 rounded-full flex-shrink-0 mt-0.5"
                    style={{ backgroundColor: SEVERITY_CONFIG[alert.severity].color }}
                  />

                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className={`entity-badge border ${SEVERITY_CONFIG[alert.severity].bg}`}>
                        {SEVERITY_CONFIG[alert.severity].label}
                      </span>
                      <span className={`text-xs font-medium ${STATUS_CONFIG[alert.status].color}`}>
                        {STATUS_CONFIG[alert.status].label}
                      </span>
                    </div>
                    <p className="text-sm font-medium text-gray-200 mt-1 truncate">{alert.title}</p>
                    <p className="text-xs text-gray-500 mt-0.5 truncate">{alert.description}</p>
                  </div>

                  <span className="text-xs text-gray-600 whitespace-nowrap flex-shrink-0">
                    {format(new Date(alert.created_at), 'MMM dd HH:mm')}
                  </span>
                </div>
              ))
            )}
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between mt-3">
              <p className="text-xs text-gray-500">
                Page {page} of {totalPages}
              </p>
              <div className="flex gap-1">
                <button
                  onClick={() => setPage(Math.max(1, page - 1))}
                  disabled={page === 1}
                  className="px-3 py-1 text-xs rounded-md bg-surface-tertiary text-gray-400 disabled:opacity-40 hover:text-gray-200"
                >
                  Previous
                </button>
                <button
                  onClick={() => setPage(Math.min(totalPages, page + 1))}
                  disabled={page === totalPages}
                  className="px-3 py-1 text-xs rounded-md bg-surface-tertiary text-gray-400 disabled:opacity-40 hover:text-gray-200"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Alert detail modal */}
      {selectedAlert && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
          <div className="w-full max-w-lg glass-panel p-6 m-4 space-y-4 animate-fade-in">
            <div className="flex items-start justify-between">
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <span className={`entity-badge border ${SEVERITY_CONFIG[selectedAlert.severity].bg}`}>
                    {SEVERITY_CONFIG[selectedAlert.severity].label}
                  </span>
                  <span className={`text-xs font-medium ${STATUS_CONFIG[selectedAlert.status].color}`}>
                    {STATUS_CONFIG[selectedAlert.status].label}
                  </span>
                </div>
                <h2 className="text-lg font-semibold text-gray-100">{selectedAlert.title}</h2>
              </div>
              <button
                onClick={() => setSelectedAlert(null)}
                className="p-1 rounded hover:bg-surface-tertiary text-gray-500 hover:text-gray-300"
              >
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <p className="text-sm text-gray-300">{selectedAlert.description}</p>

            <div className="space-y-2 text-xs border-t border-border-subtle pt-3">
              <div className="flex justify-between">
                <span className="text-gray-500">Type</span>
                <span className="text-gray-300">{selectedAlert.type}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Created</span>
                <span className="text-gray-300">{format(new Date(selectedAlert.created_at), 'MMM dd, yyyy HH:mm:ss')}</span>
              </div>
              {selectedAlert.acknowledged_at && (
                <div className="flex justify-between">
                  <span className="text-gray-500">Acknowledged</span>
                  <span className="text-gray-300">
                    {format(new Date(selectedAlert.acknowledged_at), 'MMM dd HH:mm')}
                    {selectedAlert.acknowledged_by && ` by ${selectedAlert.acknowledged_by}`}
                  </span>
                </div>
              )}
              {(selectedAlert.entity_id || (selectedAlert.entity_ids && selectedAlert.entity_ids.length > 0)) && (
                <div className="flex justify-between">
                  <span className="text-gray-500">Linked Entities</span>
                  <span className="text-gray-300">{selectedAlert.entity_ids?.length ?? (selectedAlert.entity_id ? 1 : 0)}</span>
                </div>
              )}
            </div>

            {/* Actions */}
            <div className="flex gap-2 pt-2 border-t border-border-subtle">
              {selectedAlert.status === 'new' && (
                <button
                  onClick={() => handleAction(selectedAlert.id, 'acknowledge')}
                  disabled={acknowledgeMut.isPending}
                  className="flex-1 px-4 py-2 text-sm font-medium bg-amber-500/10 text-amber-400 rounded-lg hover:bg-amber-500/20 transition-colors disabled:opacity-50"
                >
                  {acknowledgeMut.isPending ? 'Acknowledging...' : 'Acknowledge'}
                </button>
              )}
              {(selectedAlert.status === 'new' || selectedAlert.status === 'acknowledged' || selectedAlert.status === 'in_progress') && (
                <>
                  <button
                    onClick={() => handleAction(selectedAlert.id, 'resolve')}
                    disabled={resolveMut.isPending}
                    className="flex-1 px-4 py-2 text-sm font-medium bg-green-500/10 text-green-400 rounded-lg hover:bg-green-500/20 transition-colors disabled:opacity-50"
                  >
                    {resolveMut.isPending ? 'Resolving...' : 'Resolve'}
                  </button>
                  <button
                    onClick={() => handleAction(selectedAlert.id, 'dismiss')}
                    disabled={dismissMut.isPending}
                    className="flex-1 px-4 py-2 text-sm font-medium bg-surface-tertiary text-gray-400 rounded-lg hover:text-gray-200 transition-colors disabled:opacity-50"
                  >
                    Dismiss
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
